import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Loading, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, Pencil, MessageSquare } from "lucide-react";

const NUM_FIELDS = [
  ["ownership_protection_days", "Lead ownership protection (days)"],
  ["gps_interval_minutes", "GPS collection interval (minutes)"],
  ["location_retention_days", "Location retention (days)"],
  ["session_timeout_hours", "Session timeout (hours)"],
  ["gst_pct", "GST %"],
  ["password_min_length", "Minimum password length"],
];

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editTpl, setEditTpl] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const s = await api.get("/settings"); setSettings(s.data);
      const t = await api.get("/message-templates"); setTemplates(t.data);
    } catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setVal = (k, v) => setSettings((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { org_name: settings.org_name };
      NUM_FIELDS.forEach(([k]) => { payload[k] = Number(settings[k]); });
      await api.patch("/settings", payload);
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const saveTemplate = async () => {
    try {
      await api.patch(`/message-templates/${editTpl.id}`, { key: editTpl.key, name: editTpl.name, body: editTpl.body });
      toast.success("Template updated");
      setEditTpl(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!settings) return <Loading label="Loading settings…" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="Organization profile, policies and message templates." testid="settings-header"
        actions={<Button onClick={save} disabled={saving} className="bg-teal hover:bg-teal-700 h-11" data-testid="save-settings-btn"><Save className="h-4 w-4" /> Save</Button>} />

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">Organization & Policies</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Message Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6 space-y-5">
            <div><Label>Organization name</Label><Input className="mt-1.5 max-w-md" value={settings.org_name || ""} onChange={(e) => setVal("org_name", e.target.value)} data-testid="org-name-input" /></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {NUM_FIELDS.map(([k, label]) => (
                <div key={k}><Label>{label}</Label><Input type="number" className="mt-1.5" value={settings[k] ?? ""} onChange={(e) => setVal(k, e.target.value)} data-testid={`setting-${k}`} /></div>
              ))}
            </div>
            <p className="text-xs text-slate-400">No territory, zone or region settings — these are intentionally excluded from Medoho Sales OS.</p>
          </div>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <div className="space-y-3">
            {templates.map((t) => (
              <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start justify-between gap-4" data-testid={`template-${t.key}`}>
                <div className="min-w-0"><div className="font-semibold text-slate-800 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-teal" /> {t.name}</div><p className="text-sm text-slate-500 mt-1">{t.body}</p></div>
                <Button size="sm" variant="outline" onClick={() => setEditTpl(t)} data-testid={`edit-template-${t.key}`}><Pencil className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editTpl} onOpenChange={(o) => !o && setEditTpl(null)}>
        <DialogContent data-testid="template-dialog">
          <DialogHeader><DialogTitle>Edit template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input className="mt-1.5" value={editTpl?.name || ""} onChange={(e) => setEditTpl({ ...editTpl, name: e.target.value })} /></div>
            <div><Label>Body</Label><Textarea className="mt-1.5" rows={4} value={editTpl?.body || ""} onChange={(e) => setEditTpl({ ...editTpl, body: e.target.value })} data-testid="template-body-input" />
              <p className="text-xs text-slate-400 mt-1">Variables: {"{{contact_name}}, {{organization_name}}, {{user_name}}, {{demo_date}}, {{follow_up_date}}"}</p>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditTpl(null)}>Cancel</Button><Button onClick={saveTemplate} className="bg-teal hover:bg-teal-700" data-testid="save-template-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
